import { FC, useEffect, useState } from 'react';
import { Card, Col, Image, Row } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Viewer } from '@react-pdf-viewer/core';
import FleetTitle from '@/libs/antd/components/Title';

export type DocumentType = 'pdf' | 'jpg' | 'jpeg' | 'png';

export type DocumentItem = {
	type: DocumentType;
	name: string;
	url: string;
};

export type DocumentModalState = DocumentItem[] | null;

export const useDocumentModal = () => useState<DocumentModalState>(null);

export type DocumentModalProps = {
	modal: DocumentModalState;
	onClose: () => void;
};

export const renderDocument = ({ type, url }: DocumentItem) => {
	switch (type) {
		case 'pdf':
			return <Viewer fileUrl={url} />;
		case 'jpeg':
			return <Image src={url} />;
		case 'jpg':
			return <Image src={url} />;
		case 'png':
			return <Image src={url} />;
		default:
			return (
				<p>
					<a
						href={url}
						target="_blank"
					>
						Open file
					</a>
				</p>
			);
	}
};

const DocumentModal: FC<DocumentModalProps> = ({ modal, onClose }) => {
	const [activeDocument, setActiveDocument] = useState<DocumentItem | null>(
		null,
	);

	useEffect(() => {
		if (modal?.length) {
			setActiveDocument(modal[0]);
		} else {
			setActiveDocument(null);
		}

		if (!modal) return;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, [modal]);

	if (!modal) return null;

	return (
		<div
			style={{
				position: 'fixed',
				inset: '100px 0 0 300px',
				zIndex: 1000,
				padding: 30,
				background: '#FAFAFA',
				overflow: 'auto',
			}}
		>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				<div>
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={() => onClose()}
					>
						<ArrowLeftOutlined />
					</button>
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>View Document</FleetTitle>
				</div>
			</div>

			<Row
				gutter={30}
				wrap={false}
			>
				<Col
					style={{ width: 300 }}
					flex="none"
				>
					<Card
						title="Select Document"
						style={{
							position: 'sticky',
							top: 0,
						}}
					>
						{modal.map((document) => (
							<div
								role="button"
								key={document.name}
								style={{
									padding: 8,
									borderRadius: 3,
									marginBottom: 12,
									cursor: 'pointer',
									...(activeDocument?.name === document.name
										? {
												border: '1px solid #1557BC',
												color: '#1557BC',
												fontWeight: 600,
											}
										: {
												border: '1px solid #9A9A9A',
												color: '#9A9A9A',
											}),
								}}
								onClick={() => setActiveDocument(document)}
							>
								{document.name}
							</div>
						))}
					</Card>
				</Col>
				<Col flex={1}>
					{activeDocument ? renderDocument(activeDocument) : null}
				</Col>
			</Row>
		</div>
	);
};

export default DocumentModal;
